import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth.store';

const API_URL = import.meta.env.VITE_API_URL || '/api';
// Base URL without /api suffix for public endpoints (login, signup, etc.)
export const BASE_URL = API_URL.replace(/\/api$/, '');

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
// Note: We use idToken (not accessToken) because Cognito's custom claims
// (tenant_id, user_id, permissions, etc.) are in the ID token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { tokens } = useAuthStore.getState();
    if (tokens?.idToken) {
      config.headers.Authorization = `Bearer ${tokens.idToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh and subscription errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError & { _subscriptionError?: boolean }) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { tokens, updateTokens, logout } = useAuthStore.getState();

      if (tokens?.refreshToken) {
        try {
          const response = await axios.post(`${BASE_URL}/auth/refresh`, {
            refreshToken: tokens.refreshToken,
          });

          const newTokens = response.data.data.tokens;
          updateTokens(newTokens);

          originalRequest.headers.Authorization = `Bearer ${newTokens.idToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          logout();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    // If 403 due to inactive subscription, show a specific toast with action
    if (error.response?.status === 403) {
      const data = error.response.data as { error?: { message?: string } } | undefined;
      const message = data?.error?.message ?? '';
      if (message.toLowerCase().includes('assinatura')) {
        error._subscriptionError = true;
        toast.error('Acesso bloqueado — assinatura inativa', {
          description: 'Regularize o pagamento para reativar o acesso.',
          action: {
            label: 'Ver assinatura',
            onClick: () => { window.location.href = '/app/settings/subscription'; },
          },
          duration: 8000,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
